/**
 * Shop The Look - Custom Element
 * Handles drawer functionality and cart operations
 */

class ShopTheLookDrawer extends HTMLElement {
  constructor() {
    super();

    this.overlay = this.querySelector('[data-drawer-overlay]');
    this.container = this.querySelector('.shop-the-look-drawer__container');
    this.closeBtn = this.querySelector('[data-drawer-close]');
    this.body = this.querySelector('[data-drawer-body]');
    this.addAllBtn = this.querySelector('[data-add-all]');
    this.addAllPriceEl = this.querySelector('[data-add-all-price]');

    this.currentBlockIndex = null;
    this.isOpen = false;

    this.bindEvents();
    this.initTriggers();
  }

  bindEvents() {
    // Close button
    this.closeBtn?.addEventListener('click', () => this.close());

    // Overlay click - close when clicking outside drawer container (::before pseudo-element area)
    this.addEventListener('click', (e) => {
      // Close only if clicking directly on the drawer (overlay/::before area), not on container or its children
      if (e.target === this) {
        this.close();
      }
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Add all button
    this.addAllBtn?.addEventListener('click', () => this.addAllToCart());

    // Variant change events
    this.body?.addEventListener('change', (e) => {
      if (e.target.classList.contains('stl-product__variant-select')) {
        this.handleVariantChange(e.target);
      }
    });

    // Individual add to cart buttons
    this.body?.addEventListener('click', (e) => {
      const addBtn = e.target.closest('[data-add-to-cart]');
      if (addBtn) {
        this.addToCart(addBtn);
      }
    });
  }

  initTriggers() {
    // Find all trigger buttons in the parent section
    const parentSection = this.closest('[class*="shopify-section"]');
    const triggers = parentSection?.querySelectorAll('[data-stl-trigger]');

    if (!triggers || triggers.length === 0) {
      console.warn('Shop The Look: No triggers found');
      return;
    }

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const blockIndex = trigger.dataset.blockIndex;
        this.open(blockIndex);
      });
    });
  }

  open(blockIndex) {
    this.currentBlockIndex = blockIndex;

    // Hide all product groups
    const allProductGroups = this.body.querySelectorAll('[data-block-products]');
    allProductGroups.forEach((group) => {
      group.setAttribute('hidden', '');
      group.style.display = 'none';
    });

    // Show selected block's products
    const selectedGroup = this.body.querySelector(`[data-block-products="${blockIndex}"]`);
    if (selectedGroup) {
      selectedGroup.removeAttribute('hidden');
      selectedGroup.style.display = 'block';
    }

    // Update total price
    this.updateTotalPrice();

    // Open drawer
    this.classList.add('is-open');
    this.isOpen = true;
    document.body.style.overflow = 'hidden';

    // Focus management
    setTimeout(() => {
      this.closeBtn?.focus();
    }, 100);
  }

  close() {
    this.classList.remove('is-open');
    this.isOpen = false;
    document.body.style.overflow = '';
    this.currentBlockIndex = null;
  }

  handleVariantChange(select) {
    const productCard = select.closest('.stl-product');
    const selectedOption = select.options[select.selectedIndex];
    const newPrice = parseInt(selectedOption.dataset.price, 10);
    const comparePrice = selectedOption.dataset.comparePrice ? parseInt(selectedOption.dataset.comparePrice, 10) : null;
    const variantId = select.value;
    const isAvailable = selectedOption.dataset.available === 'true';

    // Update product data attribute
    productCard.dataset.productPrice = newPrice;

    // Update add to cart button
    const addBtn = productCard.querySelector('[data-add-to-cart]');
    if (addBtn) {
      addBtn.dataset.variantId = variantId;
      addBtn.disabled = !isAvailable;

      const btnText = addBtn.querySelector('.stl-product__add-btn-text');
      if (btnText) {
        btnText.textContent = isAvailable ? (window.theme?.strings?.quickAdd || 'HIZLI EKLE') : (window.theme?.strings?.soldOut || 'Tükendi');
      }
    }

    // Update price display
    const priceDisplay = productCard.querySelector('[data-product-price-display]');
    if (priceDisplay) {
      const currentPriceEl = priceDisplay.querySelector('.stl-product__current-price');
      const comparePriceEl = priceDisplay.querySelector('.stl-product__compare-price');

      if (currentPriceEl) {
        currentPriceEl.textContent = this.formatMoney(newPrice);
      }

      if (comparePriceEl) {
        if (comparePrice && comparePrice > newPrice) {
          comparePriceEl.textContent = this.formatMoney(comparePrice);
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }
    }

    // Update total price
    this.updateTotalPrice();
  }

  updateTotalPrice() {
    if (this.currentBlockIndex === null) return;

    const currentGroup = this.body.querySelector(`[data-block-products="${this.currentBlockIndex}"]`);
    if (!currentGroup) return;

    const products = currentGroup.querySelectorAll('.stl-product');
    let totalPrice = 0;

    products.forEach((product) => {
      const select = product.querySelector('.stl-product__variant-select');
      if (select) {
        const selectedOption = select.options[select.selectedIndex];
        totalPrice += parseInt(selectedOption.dataset.price, 10) || 0;
      } else {
        // Product with single variant
        totalPrice += parseInt(product.dataset.productPrice, 10) || 0;
      }
    });

    // Update button text
    if (this.addAllPriceEl) {
      this.addAllPriceEl.textContent = ' – ' + this.formatMoney(totalPrice);
    }
  }

  formatMoney(cents) {
    // Use Shopify's money format if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '{{amount_with_currency}}');
    }

    // Fallback formatting
    const amount = (cents / 100).toFixed(2);
    const currency = window.Shopify?.currency?.active || 'TL';

    // Format based on currency
    if (currency === 'TRY' || currency === 'TL') {
      return amount.replace('.', ',') + ' TL';
    }

    return currency + ' ' + amount;
  }

  async addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    // Show loading state
    button.classList.add('is-loading');
    button.disabled = true;

    try {
      const response = await fetch(window.Shopify?.routes?.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId, 10),
            quantity: 1
          }]
        })
      });

      if (!response.ok) {
        throw new Error('Add to cart failed');
      }

      // Update cart drawer if exists
      await this.refreshCart();

      // Close drawer and open cart drawer
      this.close();
      this.openCartDrawer();

    } catch (error) {
      console.error('Error adding to cart:', error);
      // Show error message if needed
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
    }
  }

  async addAllToCart() {
    if (this.currentBlockIndex === null) return;

    const currentGroup = this.body.querySelector(`[data-block-products="${this.currentBlockIndex}"]`);
    if (!currentGroup) return;

    const items = [];
    const products = currentGroup.querySelectorAll('.stl-product');

    products.forEach((product) => {
      let variantId;

      // Check for variant select
      const select = product.querySelector('.stl-product__variant-select');
      if (select) {
        variantId = select.value;
      } else {
        // Single variant product
        const hiddenInput = product.querySelector('.stl-product__variant-id');
        if (hiddenInput) {
          variantId = hiddenInput.value;
        } else {
          const addBtn = product.querySelector('[data-add-to-cart]');
          variantId = addBtn?.dataset.variantId;
        }
      }

      if (variantId) {
        items.push({
          id: parseInt(variantId, 10),
          quantity: 1
        });
      }
    });

    if (items.length === 0) return;

    // Show loading state
    this.addAllBtn.disabled = true;
    this.classList.add('is-loading');

    try {
      const response = await fetch(window.Shopify?.routes?.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        throw new Error('Add all to cart failed');
      }

      // Update cart drawer
      await this.refreshCart();

      // Close drawer and open cart drawer
      this.close();
      this.openCartDrawer();

    } catch (error) {
      console.error('Error adding all to cart:', error);
    } finally {
      this.addAllBtn.disabled = false;
      this.classList.remove('is-loading');
    }
  }

  async refreshCart() {
    // Fetch cart sections to update
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer) {
      try {
        const sections = ['cart-drawer', 'cart-counter'];

        const response = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=${sections.join(',')}`);

        if (response.ok) {
          const data = await response.json();

          // Update cart drawer body
          if (data['cart-drawer']) {
            const cartDrawerBody = document.querySelector('#CartDrawer-Body');
            if (cartDrawerBody) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(data['cart-drawer'], 'text/html');
              const newContent = doc.querySelector('#CartDrawer-Body');
              if (newContent) {
                cartDrawerBody.innerHTML = newContent.innerHTML;
              }
            }
          }

          // Update cart counter
          if (data['cart-counter']) {
            const cartCounterSection = document.querySelector('#shopify-section-cart-counter');
            if (cartCounterSection) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(data['cart-counter'], 'text/html');
              const newCounter = doc.querySelector('#shopify-section-cart-counter');
              if (newCounter) {
                cartCounterSection.innerHTML = newCounter.innerHTML;
              }
            }
          }

          // Update any cart count displays
          if (typeof updateCartCounters === 'function') {
            updateCartCounters();
          }

          // Update shipping bar if exists
          if (typeof updateFreeShipping === 'function') {
            updateFreeShipping();
          }
        }
      } catch (error) {
        console.error('Error refreshing cart:', error);
      }
    }
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      // Small delay to allow shop-the-look drawer to close first
      setTimeout(() => {
        cartDrawer.open();
      }, 100);
    }
  }
}

// Define custom element
if (!customElements.get('shop-the-look-drawer')) {
  customElements.define('shop-the-look-drawer', ShopTheLookDrawer);
}

